import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  authenticatedPilotRegistrationSchema,
  type AuthenticatedPilotRegistrationInput,
} from '@aero-judge/shared';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@aero-judge/ui';
import { ExternalLink } from 'lucide-react';
import { Layout } from '../components/Layout';
import { claimPerson, lookupPersonForClaim } from '../lib/auth-api';
import { useAuth } from '../lib/auth';
import { competitionPath, fetchCountries, registerPilot, uploadPilotPhoto } from '../lib/api';
import { useCompetition, useSlug } from '../hooks/useCompetition';
import { isRegistrationPhase, showPublicRegistration } from '../lib/competitionStatus';

type AuthMode = 'login' | 'signup';

type CompetitionRegistrationForm = {
  club: string;
  glider: string;
  harness: string;
  emergencyContact: string;
  emergencyPhone: string;
  dateOfBirth: string;
  countryCode: string;
  faiLicense: string;
  civlId: string;
  firstName: string;
  lastName: string;
  gender: 'MALE' | 'FEMALE' | 'OTHER';
};

const competitionFieldsEmpty: CompetitionRegistrationForm = {
  club: '',
  glider: '',
  harness: '',
  emergencyContact: '',
  emergencyPhone: '',
  dateOfBirth: '',
  countryCode: '',
  faiLicense: '',
  civlId: '',
  firstName: '',
  lastName: '',
  gender: 'MALE',
};

const PHOTO_MAX_BYTES = 2 * 1024 * 1024;

function RegistrationQr({ url, label }: { url: string; label: string }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void QRCode.toDataURL(url, { width: 280, margin: 1, errorCorrectionLevel: 'M' })
      .then((data) => {
        if (!cancelled) setSrc(data);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!src) {
    return <div className="h-44 w-44 rounded-lg bg-muted" aria-hidden />;
  }

  return (
    <img src={src} alt={`QR code for ${label}`} className="h-44 w-44 rounded-lg bg-white p-2" />
  );
}

export function RegisterPage() {
  const competitionId = useSlug();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: competition, isLoading: compLoading } = useCompetition();
  const registrationOpen = showPublicRegistration(competition);
  const registrationExternal = competition?.settings?.publicRegistrationEnabled === false;
  const {
    user,
    isLoading: authLoading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshMe,
  } = useAuth();

  const { data: countries = [] } = useQuery({
    queryKey: ['public-countries'],
    queryFn: fetchCountries,
  });

  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [authError, setAuthError] = useState<string | null>(null);
  const [authBusy, setAuthBusy] = useState(false);

  const [claimId, setClaimId] = useState('');
  const [claimMsg, setClaimMsg] = useState<string | null>(null);
  const [claimBusy, setClaimBusy] = useState(false);

  const [form, setForm] = useState<CompetitionRegistrationForm>(competitionFieldsEmpty);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const person = user?.person;
  const hasPerson = !!person;
  const profilePhotoUrl = person?.photoUrl ?? null;
  const displayPhotoUrl =
    photoFile &&
    (photoFile.type.toLowerCase().startsWith('image/hei') || /\.hei[cf]$/i.test(photoFile.name))
      ? null
      : (photoPreview ?? profilePhotoUrl);

  const mutation = useMutation({
    mutationFn: async (body: AuthenticatedPilotRegistrationInput) => {
      const pilot = await registerPilot(competitionId, body);
      if (photoFile) {
        try {
          await uploadPilotPhoto(competitionId, pilot.id, photoFile);
        } catch {
          // Registration still succeeds if photo upload fails (e.g. Cloudinary not configured).
        }
      }
      return pilot;
    },
    onSuccess: (pilot) => {
      queryClient.invalidateQueries({ queryKey: ['public-pilots', competitionId] });
      void refreshMe();
      navigate(competitionPath(competitionId, 'pilots', String(pilot.pilotNumber)), {
        state: { justRegistered: true },
      });
    },
  });

  const onPhotoChange = (file: File | null) => {
    if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    if (!file) {
      setPhotoFile(null);
      setPhotoPreview(null);
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      setFieldError('Photo is too large. Maximum size is 2 MB.');
      return;
    }
    setFieldError(null);
    setPhotoFile(file);
    const type = file.type.toLowerCase();
    const heic =
      type.startsWith('image/heic') ||
      type.startsWith('image/heif') ||
      /\.hei[cf]$/i.test(file.name);
    setPhotoPreview(heic ? null : URL.createObjectURL(file));
  };
  const onAuthSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setAuthBusy(true);
    try {
      if (authMode === 'login') {
        await login(authForm.email, authForm.password);
      } else {
        await register({
          email: authForm.email,
          password: authForm.password,
          firstName: authForm.firstName,
          lastName: authForm.lastName,
        });
      }
      setForm((f) => ({
        ...f,
        firstName: authForm.firstName || f.firstName,
        lastName: authForm.lastName || f.lastName,
      }));
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setAuthBusy(false);
    }
  };

  const onClaim = async (e: FormEvent) => {
    e.preventDefault();
    setClaimMsg(null);
    setClaimBusy(true);
    try {
      const q = claimId.trim();
      const params = q.toUpperCase().startsWith('AJ-') ? { aeroJudgeId: q } : { civlId: q };
      const lookup = await lookupPersonForClaim(params);
      if (!lookup.matches.length) {
        setClaimMsg('No profile found for that AeroJudge ID or CIVL ID.');
        return;
      }
      const match = lookup.matches[0]!;
      const result = await claimPerson({ personId: match.person.id });
      if (result.status === 'CLAIMED' || result.status === 'ALREADY_LINKED') {
        await refreshMe();
        setClaimMsg(
          `Linked to ${result.person.firstName} ${result.person.lastName} (${result.person.aeroJudgeId}).`,
        );
      } else {
        setClaimMsg(
          result.message ??
            'Claim submitted for organiser approval. You can still create a new profile to register now.',
        );
      }
    } catch (err) {
      setClaimMsg(err instanceof Error ? err.message : 'Claim failed');
    } finally {
      setClaimBusy(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFieldError(null);

    const payload: AuthenticatedPilotRegistrationInput = {
      club: form.club || undefined,
      glider: form.glider || undefined,
      harness: form.harness || undefined,
      emergencyContact: form.emergencyContact || undefined,
      emergencyPhone: form.emergencyPhone || undefined,
      dateOfBirth: form.dateOfBirth || undefined,
      countryCode: form.countryCode || undefined,
      faiLicense: form.faiLicense || undefined,
      civlId: form.civlId || undefined,
    };

    if (!hasPerson) {
      payload.firstName = form.firstName || user?.firstName;
      payload.lastName = form.lastName || user?.lastName;
      payload.gender = form.gender;
    }

    const parsed = authenticatedPilotRegistrationSchema.safeParse(payload);
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Please check the form');
      return;
    }
    if (!hasPerson && (!parsed.data.firstName || !parsed.data.lastName)) {
      setFieldError('First and last name are required to create your AeroJudge profile');
      return;
    }
    mutation.mutate(parsed.data);
  };

  if (compLoading || authLoading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-sky-400 border-t-transparent" />
        </div>
      </Layout>
    );
  }

  if (!registrationOpen && registrationExternal && isRegistrationPhase(competition)) {
    const links = competition?.registrationLinks ?? [];
    return (
      <Layout>
        <div className="mx-auto max-w-5xl px-6 py-16">
          <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Registration
          </p>
          <h1 className="mt-2 font-display text-4xl font-bold text-foreground">
            Pilot registration
          </h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Use a link or scan a QR code. Pilots for this competition are entered from the
            organiser’s list.
          </p>
          {links.length === 0 ? (
            <p className="mt-10 text-muted-foreground">
              Registration links have not been published yet.
            </p>
          ) : (
            <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {links.map((link) => (
                <li
                  key={link.id}
                  className="flex flex-col items-center rounded-2xl border border-border bg-card px-6 py-8 text-center"
                >
                  <RegistrationQr url={link.url} label={link.label} />
                  <h2 className="mt-5 font-display text-xl font-semibold text-foreground">
                    {link.label}
                  </h2>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 underline-offset-2 hover:underline dark:text-sky-400"
                  >
                    Open link
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Layout>
    );
  }

  if (!registrationOpen) {
    return (
      <Layout>
        <div className="mx-auto max-w-lg px-6 py-24 text-center">
          <h1 className="font-display text-3xl text-foreground">Registration closed</h1>
          <p className="mt-3 text-muted-foreground">
            Pilot self-registration is not open for this competition right now.
          </p>
          <Link
            to={competitionPath(competitionId, 'pilots')}
            className="mt-8 inline-block text-sky-700 underline hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300"
          >
            View registered pilots
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-6 py-12">
        <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground">
          {competition?.name}
        </p>
        <h1 className="mt-2 font-display text-4xl font-bold text-foreground">Pilot registration</h1>
        <p className="mt-3 text-muted-foreground">
          Create an AeroJudge account or sign in. If organisers already added you, claim that
          profile with your AJ-… or CIVL ID, then enter only this competition&apos;s details.
        </p>

        {/* Step 1: Auth */}
        {!isAuthenticated ? (
          <div className="mt-10 rounded-xl border border-border bg-muted/40 p-6">
            <div className="mb-6 flex gap-2">
              <Button
                type="button"
                variant={authMode === 'login' ? 'default' : 'outline'}
                className={
                  authMode === 'login'
                    ? 'bg-sky-500 text-[#050d1a] hover:bg-sky-400'
                    : 'border-input bg-transparent text-foreground'
                }
                onClick={() => setAuthMode('login')}
              >
                Sign in
              </Button>
              <Button
                type="button"
                variant={authMode === 'signup' ? 'default' : 'outline'}
                className={
                  authMode === 'signup'
                    ? 'bg-sky-500 text-[#050d1a] hover:bg-sky-400'
                    : 'border-input bg-transparent text-foreground'
                }
                onClick={() => setAuthMode('signup')}
              >
                Create account
              </Button>
            </div>
            <form onSubmit={onAuthSubmit} className="space-y-4">
              {authMode === 'signup' && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-foreground">First name *</Label>
                    <Input
                      required
                      value={authForm.firstName}
                      onChange={(e) => setAuthForm((f) => ({ ...f, firstName: e.target.value }))}
                      className="border-border bg-card text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Last name *</Label>
                    <Input
                      required
                      value={authForm.lastName}
                      onChange={(e) => setAuthForm((f) => ({ ...f, lastName: e.target.value }))}
                      className="border-border bg-card text-foreground"
                    />
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-foreground">Email *</Label>
                <Input
                  type="email"
                  required
                  autoComplete="email"
                  value={authForm.email}
                  onChange={(e) => setAuthForm((f) => ({ ...f, email: e.target.value }))}
                  className="border-border bg-card text-foreground"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-foreground">Password *</Label>
                  {authMode === 'login' ? (
                    <Link
                      to={`/forgot-password?${new URLSearchParams({
                        returnTo: competitionPath(competitionId, 'register'),
                        ...(authForm.email.trim() ? { email: authForm.email.trim() } : {}),
                      }).toString()}`}
                      className="text-xs font-medium text-sky-700 dark:text-sky-400 hover:underline"
                    >
                      Forgot password?
                    </Link>
                  ) : null}
                </div>
                <Input
                  type="password"
                  required
                  minLength={8}
                  autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                  value={authForm.password}
                  onChange={(e) => setAuthForm((f) => ({ ...f, password: e.target.value }))}
                  className="border-border bg-card text-foreground"
                />
              </div>
              {authError && (
                <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                  {authError}
                </p>
              )}
              <Button
                type="submit"
                disabled={authBusy}
                className="bg-sky-500 text-[#050d1a] hover:bg-sky-400"
              >
                {authBusy
                  ? 'Please wait…'
                  : authMode === 'login'
                    ? 'Sign in to continue'
                    : 'Create account & continue'}
              </Button>
            </form>
          </div>
        ) : (
          <>
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-sky-500/20 bg-sky-500/5 px-4 py-3">
              <div className="text-sm text-foreground">
                Signed in as <span className="font-medium text-foreground">{user?.email}</span>
                {person && (
                  <span className="mt-1 block text-muted-foreground">
                    Profile: {person.firstName} {person.lastName} · {person.aeroJudgeId}
                    {person.civlId ? ` · CIVL ${person.civlId}` : ''}
                  </span>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={logout}
                className="border-input bg-transparent text-foreground"
              >
                Sign out
              </Button>
            </div>

            {/* Step 2: Claim existing profile if not linked */}
            {!hasPerson && (
              <div className="mt-6 rounded-xl border border-border bg-muted/40 p-6">
                <h2 className="text-lg font-semibold text-foreground">
                  Claim an existing profile?
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Already in the pilot list (added by organisers)? Enter your AeroJudge ID (AJ-…) or
                  CIVL ID to link that Person instead of creating a duplicate. Instant claim works
                  when the profile email matches your login email; otherwise an organiser must
                  approve.
                </p>
                <form onSubmit={onClaim} className="mt-4 flex flex-wrap gap-3">
                  <Input
                    placeholder="AJ-XXXXXX or CIVL ID"
                    value={claimId}
                    onChange={(e) => setClaimId(e.target.value)}
                    className="max-w-xs border-border bg-card text-foreground"
                  />
                  <Button
                    type="submit"
                    disabled={claimBusy || !claimId.trim()}
                    variant="outline"
                    className="border-sky-500/40 bg-transparent text-foreground"
                  >
                    {claimBusy ? 'Looking up…' : 'Find & claim'}
                  </Button>
                </form>
                {claimMsg && <p className="mt-3 text-sm text-foreground/90">{claimMsg}</p>}
                <p className="mt-4 text-xs text-muted-foreground">
                  New to AeroJudge? Skip this and we&apos;ll create your profile when you submit
                  registration below.
                </p>
              </div>
            )}

            {/* Step 3: Competition registration */}
            <form onSubmit={onSubmit} className="mt-10 space-y-6">
              <h2 className="text-lg font-semibold text-foreground">
                {hasPerson ? 'Competition details' : 'Create profile & competition details'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {hasPerson
                  ? 'Identity is taken from your AeroJudge profile. Enter only event-specific information.'
                  : 'We’ll create your AeroJudge Person profile, then register you in this competition.'}
              </p>

              {!hasPerson && (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="text-foreground">First name *</Label>
                      <Input
                        required
                        value={form.firstName || user?.firstName || ''}
                        onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                        className="border-border bg-card text-foreground"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Last name *</Label>
                      <Input
                        required
                        value={form.lastName || user?.lastName || ''}
                        onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                        className="border-border bg-card text-foreground"
                      />
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="text-foreground">Gender *</Label>
                      <Select
                        value={form.gender}
                        onValueChange={(v) =>
                          setForm((f) => ({
                            ...f,
                            gender: v as 'MALE' | 'FEMALE' | 'OTHER',
                          }))
                        }
                      >
                        <SelectTrigger className="border-border bg-card text-foreground">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="border-sky-500/30 bg-muted text-popover-foreground shadow-xl">
                          <SelectItem value="MALE">Male</SelectItem>
                          <SelectItem value="FEMALE">Female</SelectItem>
                          <SelectItem value="OTHER">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Country</Label>
                      <Select
                        value={form.countryCode || '__none__'}
                        onValueChange={(v) =>
                          setForm((f) => ({
                            ...f,
                            countryCode: v === '__none__' ? '' : v,
                          }))
                        }
                      >
                        <SelectTrigger className="border-border bg-card text-foreground">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent className="border-sky-500/30 bg-muted text-popover-foreground shadow-xl">
                          <SelectItem value="__none__">Not specified</SelectItem>
                          {countries.map((c) => (
                            <SelectItem key={c.id} value={c.code}>
                              {c.name} ({c.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="text-foreground">CIVL ID</Label>
                      <Input
                        value={form.civlId}
                        onChange={(e) => setForm((f) => ({ ...f, civlId: e.target.value }))}
                        className="border-border bg-card text-foreground"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">FAI license</Label>
                      <Input
                        value={form.faiLicense}
                        onChange={(e) => setForm((f) => ({ ...f, faiLicense: e.target.value }))}
                        className="border-border bg-card text-foreground"
                      />
                    </div>
                  </div>
                </>
              )}

              {hasPerson && (
                <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-foreground">
                  <p className="font-medium text-foreground">
                    {person!.firstName} {person!.lastName}
                  </p>
                  <p className="text-muted-foreground">
                    {person!.aeroJudgeId}
                    {person!.civlId ? ` · CIVL ${person!.civlId}` : ''}
                    {person!.nationalityCountry?.code
                      ? ` · ${person!.nationalityCountry.code}`
                      : ''}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-foreground">Photo (optional)</Label>
                <p className="text-xs text-muted-foreground">
                  {photoFile
                    ? 'New photo will be used for leaderboards and venue display · PNG, JPEG, WebP, GIF, or HEIC · max 2 MB'
                    : profilePhotoUrl
                      ? 'Using your AeroJudge profile photo · choose a file to replace'
                      : 'Headshot for leaderboards and venue display · PNG, JPEG, WebP, GIF, or HEIC · max 2 MB'}
                </p>
                <div className="flex items-center gap-4">
                  {displayPhotoUrl ? (
                    <img
                      src={displayPhotoUrl}
                      alt=""
                      className="h-16 w-16 rounded-full object-cover object-top ring-1 ring-white/20"
                    />
                  ) : (
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-muted text-xs text-muted-foreground">
                      {photoFile ? 'Added' : 'None'}
                    </span>
                  )}
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif,image/heic,image/heif,.heic,.heif"
                    className="border-border bg-card text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-sky-500 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-[#050d1a]"
                    onChange={(e) => onPhotoChange(e.target.files?.[0] ?? null)}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-foreground">Club / team</Label>
                  <Input
                    value={form.club}
                    onChange={(e) => setForm((f) => ({ ...f, club: e.target.value }))}
                    className="border-border bg-card text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Date of birth</Label>
                  <Input
                    type="date"
                    value={form.dateOfBirth}
                    onChange={(e) => setForm((f) => ({ ...f, dateOfBirth: e.target.value }))}
                    className="border-border bg-card text-foreground"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-foreground">Glider</Label>
                  <Input
                    value={form.glider}
                    onChange={(e) => setForm((f) => ({ ...f, glider: e.target.value }))}
                    className="border-border bg-card text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Harness</Label>
                  <Input
                    value={form.harness}
                    onChange={(e) => setForm((f) => ({ ...f, harness: e.target.value }))}
                    className="border-border bg-card text-foreground"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-foreground">Emergency contact</Label>
                  <Input
                    value={form.emergencyContact}
                    onChange={(e) => setForm((f) => ({ ...f, emergencyContact: e.target.value }))}
                    className="border-border bg-card text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Emergency phone</Label>
                  <Input
                    value={form.emergencyPhone}
                    onChange={(e) => setForm((f) => ({ ...f, emergencyPhone: e.target.value }))}
                    className="border-border bg-card text-foreground"
                  />
                </div>
              </div>

              {(fieldError || mutation.isError) && (
                <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                  {fieldError ??
                    (mutation.error instanceof Error
                      ? mutation.error.message
                      : 'Registration failed')}
                </p>
              )}

              <div className="flex flex-wrap gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={mutation.isPending}
                  className="bg-sky-500 text-[#050d1a] hover:bg-sky-400"
                >
                  {mutation.isPending ? 'Submitting…' : 'Submit registration'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  asChild
                  className="border-input bg-transparent text-foreground hover:bg-muted hover:text-foreground"
                >
                  <Link to={competitionPath(competitionId, 'pilots')}>Cancel</Link>
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </Layout>
  );
}
